import i18next from "i18next";
import React, { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import Button from "components/button";
import Loader from "components/loader";
import { MODALS } from "common";
import { setAppModal } from "redux/slices/appSlice";
import { fetchUsers } from "redux/slices/usersSlice";
import usersService from "services/users.service";
import Utils from "common/utils";
import css from "./tenantMemberInvite.module.scss";

export default function TenantMemberInvite() {
  const dispatch = useDispatch();
  const [emailsInput, setEmailsInput] = useState("");
  const [errors, setErrors] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  const close = useCallback(() => {
    if (!isSaving) dispatch(setAppModal(MODALS.NONE));
  }, [dispatch, isSaving]);

  const submit = useCallback(async (event) => {
    event.preventDefault();
    const emails = emailsInput
      .split(",")
      .map((email) => email.trim().toLowerCase())
      .filter(Boolean);
    if (emails.length === 0) {
      setErrors([i18next.t("labels.invite_email_required")]);
      return;
    }

    const emailCounts = emails.reduce((counts, email) => {
      counts[email] = (counts[email] || 0) + 1;
      return counts;
    }, {});
    const validationErrors = [];
    emails.forEach((email) => {
      if (!Utils.validateEmail(email)) {
        validationErrors.push(`${email}: ${i18next.t("labels.invite_email_invalid")}`);
      } else if (emailCounts[email] > 1) {
        validationErrors.push(`${email}: ${i18next.t("labels.invite_duplicate_email")}`);
      }
    });
    if (validationErrors.length > 0) {
      setErrors([...new Set(validationErrors)]);
      return;
    }

    setIsSaving(true);
    setErrors([]);
    try {
      await usersService.addTenantMembers(emails);
      dispatch(fetchUsers());
      dispatch(setAppModal(MODALS.NONE));
    } catch (requestError) {
      const responseErrors = requestError.response?.data?.errors;
      const responseMessage = requestError.response?.data?.message;
      setErrors(
        Array.isArray(responseErrors)
          ? responseErrors
          : [responseMessage || i18next.t("labels.tenant_member_invite_failed")]
      );
    } finally {
      setIsSaving(false);
    }
  }, [dispatch, emailsInput]);

  return (
    <div className={css.container} role="dialog" aria-modal="true" aria-labelledby="tenant-member-invite-title">
      <form onSubmit={submit}>
        <div className={css.header}>
          <div className={css.title} id="tenant-member-invite-title">
            {i18next.t("labels.tenant_member_invite_title")}
          </div>
        </div>

        <label className={css.label} htmlFor="tenant-member-emails">
          {i18next.t("labels.email_address")}
        </label>
        <textarea
          id="tenant-member-emails"
          className={css.input}
          rows={3}
          autoFocus
          value={emailsInput}
          placeholder={i18next.t("labels.tenant_member_emails_placeholder")}
          onChange={(event) => {
            setEmailsInput(event.target.value);
            setErrors([]);
          }}
          disabled={isSaving}
          aria-invalid={errors.length > 0}
          aria-describedby={errors.length > 0 ? "tenant-member-invite-errors" : undefined}
        />
        {errors.length > 0 && (
          <div className={css.errors} id="tenant-member-invite-errors" role="alert">
            {errors.map((error, index) => (
              <div key={`${error}-${index}`}>{error}</div>
            ))}
          </div>
        )}

        <div className={css.actions}>
          <Button
            title={i18next.t("buttons.cancel")}
            className={css.secondary}
            onPress={close}
            disabled={isSaving}
          />
          <Button
            title={i18next.t("buttons.add_tenant_member")}
            onPress={submit}
            disabled={isSaving}
          />
        </div>
        {isSaving && <Loader />}
      </form>
    </div>
  );
}
